import {
  InputWidth,
  ReadOnlyField,
  SelectType,
  Typeahead,
  TypeaheadOption,
  TypeaheadOptionsProps,
} from '@msaf/core-react'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useLookup } from '../../hooks/use-api'

const DEFAULT_TYPEAHEAD_OPTION = { label: '', value: '' }

export interface LookupFieldProps extends Partial<Omit<TypeaheadOptionsProps, 'value' | 'setValue'>> {
  fieldId: string
  fieldFilters?: Record<string, string | number | boolean | null>
  value?: TypeaheadOption | number | string
  setValue: (value: TypeaheadOption) => void
  handleChangeCallback?: (option: TypeaheadOption) => void
  setValueOnInitialLoad?: Dispatch<SetStateAction<any>>
  token?: string
  isClearable?: boolean
  isSkeleton?: boolean
  showNameAndDescription?: boolean
  selectType?: SelectType
  validationMessages?: Array<string>
  fieldSize?: InputWidth
}

interface LookupFieldOption extends TypeaheadOption {
  description: string
}

export default function LookupField({
  fieldId,
  fieldFilters,
  value = DEFAULT_TYPEAHEAD_OPTION,
  setValue,
  labelledBy,
  isClearable,
  isSkeleton,
  showNameAndDescription,
  isInvalid,
  handleChangeCallback,
  setValueOnInitialLoad,
  menuPortalTarget,
  selectType,
  isDisabled,
  validationMessages,
  fieldSize,
}: LookupFieldProps) {
  const [selectedOption, setSelectedOption] = useState<TypeaheadOption>(DEFAULT_TYPEAHEAD_OPTION)
  const [options, setOptions] = useState<LookupFieldOption[]>([])

  const { isLoading: isLoadingOptions, isSuccess, data } = useLookup(fieldId, fieldFilters)

  useEffect(() => {
    if (options.length && value) {
      // Find the passed value in the lookup options and set the selected option
      const selected = options.find((opt: TypeaheadOption) => String(opt.value) === String(value))
      if (selected) {
        setSelectedOption(selected)
        setValueOnInitialLoad && setValueOnInitialLoad(selected)
      } else {
        setSelectedOption(DEFAULT_TYPEAHEAD_OPTION)
      }
    } else {
      setSelectedOption(DEFAULT_TYPEAHEAD_OPTION)
    }
  }, [options, value, setValueOnInitialLoad])

  useEffect(() => {
    if (isSuccess && data) {
      const opts: LookupFieldOption[] = data.map(({ id, name, description, filters }: LookupFieldOption) => ({
        label: showNameAndDescription ? `${name} - ${description}` : name,
        value: id,
        filters: filters ? filters : [],
      }))
      setOptions(opts)
    }
  }, [isSuccess, data, setOptions, showNameAndDescription])

  const handleChange = (option: TypeaheadOption | null) => {
    // Need to check existence of both label and value otherwise selected option being set as {} - empty object
    if (option !== null && ['number', 'string'].includes(typeof option?.value)) {
      setSelectedOption(option)
      handleChangeCallback && handleChangeCallback(option)
      setValue(option)
    } else {
      setSelectedOption(DEFAULT_TYPEAHEAD_OPTION)
      setValue(DEFAULT_TYPEAHEAD_OPTION)
    }
  }

  if (isDisabled) {
    return (
      <ReadOnlyField
        isSkeleton={isLoadingOptions || isSkeleton}
        labelledBy={labelledBy ?? fieldId}
        value={selectedOption.label}
      />
    )
  }

  return (
    <div data-test-typeahead-selector={labelledBy}>
      <Typeahead
        isInvalid={isInvalid}
        selectType={selectType ?? 'default'}
        labelledBy={labelledBy ?? fieldId}
        options={options}
        handleChange={handleChange}
        selectedOption={selectedOption}
        isSkeleton={isSkeleton || isLoadingOptions}
        isClearable={isClearable}
        menuPortalTarget={menuPortalTarget}
        isDisabled={isDisabled}
        fieldSize={fieldSize}
      />
      {validationMessages?.map((errorMessage, idx) => (
        <div key={`${idx}--validation-msg`} className='c-form-field__validation-msg' role='alert'>
          {errorMessage}
        </div>
      ))}
    </div>
  )
}
